import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { pricingScenarioService } from '../../../services/financeService';
import { PricingScenario } from '../../../types/finance';
import { FaSave, FaTrash, FaPlus } from 'react-icons/fa';
import toast from 'react-hot-toast';

const CalculatorTab: React.FC = () => {
  const { user } = useAuth();
  const [scenarios, setScenarios] = useState<PricingScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentScenario, setCurrentScenario] = useState<PricingScenario>({
    name: '',
    product_name: '',
    cost_per_unit: 0,
    fixed_costs: 0,
    desired_margin: 0.3,
    recommended_price: 0,
    pricing_strategy: 'cost-plus',
  });

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      const data = await pricingScenarioService.getAll();
      setScenarios(data);
    } catch (error) {
      console.error('Error loading pricing scenarios:', error);
      toast.error('Failed to load pricing scenarios');
    } finally {
      setLoading(false);
    }
  };

  const calculateRecommendedPrice = (scenario: PricingScenario): number => {
    const { cost_per_unit, fixed_costs, desired_margin } = scenario;
    const totalCostPerUnit = cost_per_unit + (fixed_costs / 100);
    const price = totalCostPerUnit / (1 - desired_margin);
    return parseFloat(price.toFixed(2));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    if (['cost_per_unit', 'fixed_costs', 'desired_margin', 'competitor_price'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentScenario(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const recommended_price = calculateRecommendedPrice(currentScenario);
      const scenarioToSave = {
        ...currentScenario,
        user_id: user?.id,
        recommended_price
      };
      
      if (currentScenario.id) {
        await pricingScenarioService.update(currentScenario.id, scenarioToSave);
        toast.success('Pricing scenario updated');
      } else {
        await pricingScenarioService.create(scenarioToSave);
        toast.success('Pricing scenario created');
      }
      
      setShowForm(false);
      setCurrentScenario({
        name: '',
        product_name: '',
        cost_per_unit: 0,
        fixed_costs: 0,
        desired_margin: 0.3,
        recommended_price: 0,
        pricing_strategy: 'cost-plus',
      });
      loadScenarios();
    } catch (error) {
      console.error('Error saving pricing scenario:', error);
      toast.error('Failed to save pricing scenario');
    }
  };

  const handleEdit = (scenario: PricingScenario) => {
    setCurrentScenario(scenario);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this pricing scenario?')) {
      try {
        await pricingScenarioService.delete(id);
        toast.success('Pricing scenario deleted');
        loadScenarios();
      } catch (error) {
        console.error('Error deleting pricing scenario:', error);
        toast.error('Failed to delete pricing scenario');
      }
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading scenarios...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="rounded-xl bg-blue-50 p-5 shadow-sm ring-1 ring-blue-100">
        <h3 className="mb-2 text-base font-semibold text-blue-900">Cost-Plus Pricing Calculator</h3>
        <p className="mb-3 text-sm text-blue-800">
          Calculate optimal prices using the cost-plus method. Enter your costs and desired margin to get a recommended price.
        </p>
        <ul className="space-y-1 text-sm text-blue-700">
          <li className="flex items-center gap-2">
            <span className="text-blue-500">•</span>
            <span>Cost per unit: Direct costs (materials, labor)</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-blue-500">•</span>
            <span>Fixed costs: Overhead expenses (rent, utilities)</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-blue-500">•</span>
            <span>Desired margin: Target profit percentage</span>
          </li>
        </ul>
      </div>

      {/* Create Button */}
      {!showForm && (
        <button
          onClick={() => {
            setCurrentScenario({
              name: '',
              product_name: '',
              cost_per_unit: 0,
              fixed_costs: 0,
              desired_margin: 0.3,
              recommended_price: 0,
              pricing_strategy: 'cost-plus',
            });
            setShowForm(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          <FaPlus /> Create New Pricing Scenario
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div className="rounded-xl bg-gray-50 p-6 shadow-sm ring-1 ring-gray-200">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            {currentScenario.id ? 'Edit' : 'Create'} Pricing Scenario
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Scenario Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentScenario.name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Summer Product Line"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product/Service Name *</label>
                <input
                  type="text"
                  name="product_name"
                  value={currentScenario.product_name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Handmade Soap"
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cost Per Unit ($) *</label>
                <input
                  type="number"
                  name="cost_per_unit"
                  value={currentScenario.cost_per_unit}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="5.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Fixed Costs ($) *</label>
                <input
                  type="number"
                  name="fixed_costs"
                  value={currentScenario.fixed_costs}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="100.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Desired Margin * </label>
                <input
                  type="number"
                  name="desired_margin"
                  value={currentScenario.desired_margin}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="0.30"
                  min="0"
                  max="0.99"
                  step="0.01"
                  required
                />
                <span className="mt-1 block text-xs text-gray-500">As decimal (e.g., 0.3 for 30%)</span>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Competitor Price ($)</label>
                <input
                  type="number"
                  name="competitor_price"
                  value={currentScenario.competitor_price || ''}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="12.99"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Pricing Strategy *</label>
                <select
                  name="pricing_strategy"
                  value={currentScenario.pricing_strategy}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                >
                  <option value="cost-plus">Cost-Plus Pricing</option>
                  <option value="value-based">Value-Based Pricing</option>
                  <option value="competitive">Competitive Pricing</option>
                  <option value="premium">Premium Pricing</option>
                  <option value="economy">Economy Pricing</option>
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
              <input
                type="text"
                name="notes"
                value={currentScenario.notes || ''}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="Additional information"
              />
            </div>

            {/* Price Preview */}
            {currentScenario.cost_per_unit > 0 && (
              <div className="rounded-xl bg-green-50 p-4 shadow-sm ring-1 ring-green-200">
                <h4 className="mb-2 text-base font-semibold text-green-900">Price Preview</h4>
                <p className="text-sm text-green-800">
                  Recommended price: 
                  <span className="ml-2 text-xl font-bold text-green-600">
                    ${calculateRecommendedPrice(currentScenario).toFixed(2)}
                  </span>
                </p>
                <p className="mt-1 text-xs text-green-700">
                  This price gives you a {(currentScenario.desired_margin * 100).toFixed(0)}% profit margin
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              {currentScenario.id && (
                <button
                  type="button"
                  onClick={() => handleDelete(currentScenario.id!)}
                  className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <FaTrash /> Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
              >
                <FaSave /> {currentScenario.id ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Scenarios Table */}
      {scenarios.length > 0 && (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Cost/Unit</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Margin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Recommended Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Strategy</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {scenarios.map((scenario) => (
                  <tr key={scenario.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{scenario.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{scenario.product_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">
                        ${scenario.cost_per_unit.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-purple-50 px-2 py-1 font-medium text-purple-700">
                        {(scenario.desired_margin * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="rounded-full bg-green-50 px-2 py-1 font-bold text-green-700">
                        ${scenario.recommended_price.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {scenario.pricing_strategy.replace('-', ' ')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(scenario)}
                          className="text-primary hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(scenario.id!)}
                          className="text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {scenarios.length === 0 && !showForm && (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">🧮</div>
          <h3 className="text-lg font-semibold text-gray-900">No Pricing Scenarios Yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create your first pricing scenario to start calculating optimal prices
          </p>
        </div>
      )}
    </div>
  );
};

export default CalculatorTab;
